import React, { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { X, ArrowsOut } from '@phosphor-icons/react';

/**
 * Avatar cropper modal — drag/zoom on a circle, exports a 384x384 PNG and
 * uploads to /api/auth/avatar (which writes to /api/static/avatars/{id}.png).
 */
const AvatarCropper = ({ open, onClose, onSaved }) => {
  const { refresh } = useAuth();
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPx, setAreaPx] = useState(null);
  const [busy, setBusy] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Pick an image'); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error('Image too large (8MB max)'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setSrc(ev.target.result);
    reader.readAsDataURL(f);
  };

  const onCropDone = useCallback((_a, px) => setAreaPx(px), []);

  const exportAndUpload = async () => {
    if (!src || !areaPx) return;
    setBusy(true);
    try {
      const img = await new Promise((res, rej) => {
        const i = new Image();
        i.onload = () => res(i);
        i.onerror = rej;
        i.src = src;
      });
      const canvas = document.createElement('canvas');
      const size = 384;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img,
        areaPx.x, areaPx.y, areaPx.width, areaPx.height,
        0, 0, size, size);
      const b64 = canvas.toDataURL('image/png');
      const { data } = await api.post('/auth/avatar', { avatar_b64: b64 });
      toast.success('Avatar updated');
      await refresh();
      onSaved?.(data.avatar_url);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'upload failed');
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-xl grid place-items-center px-4"
         data-testid="avatar-cropper">
      <div className="w-full max-w-md bg-[#0a0a14] rounded-3xl p-6 relative ring-1 ring-white/10">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full glass-hi text-white/70 hover:text-white"
                data-testid="avatar-cropper-close" aria-label="close">
          <X size={18} weight="bold" />
        </button>
        <h3 className="font-display text-2xl font-black tracking-tight">Choose your avatar</h3>

        {!src ? (
          <label className="block mt-6 border-2 border-dashed border-white/15 rounded-2xl p-10 text-center cursor-pointer hover:border-bx-cyan transition"
                 data-testid="avatar-cropper-dropzone">
            <ArrowsOut size={28} weight="duotone" className="mx-auto text-bx-cyan" />
            <div className="text-sm mt-2">Click to pick an image</div>
            <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">png · jpg · webp · max 8MB</div>
            <input type="file" accept="image/*" className="hidden" onChange={onFile}
                   data-testid="avatar-file-input" />
          </label>
        ) : (
          <>
            <div className="relative h-80 mt-5 rounded-2xl overflow-hidden bg-black">
              <Cropper
                image={src}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropDone}
              />
            </div>
            <div className="mt-4 flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-widest text-white/60">zoom</span>
              <input
                type="range" min={1} max={3} step={0.05} value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1 accent-bx-cyan"
                data-testid="avatar-zoom"
              />
            </div>
            <div className="mt-5 flex gap-3">
              <button onClick={() => setSrc(null)} className="px-4 py-2.5 rounded-full glass-hi text-sm font-bold text-white/70 hover:bg-white/10">
                Pick another
              </button>
              <button onClick={exportAndUpload} disabled={busy}
                      data-testid="avatar-save"
                      className="btn-primary px-5 py-2.5 rounded-full text-sm flex-1 disabled:opacity-50">
                {busy ? 'Uploading…' : 'Save avatar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AvatarCropper;
